import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite, list } from '../lib/api'
import { Empty, PageHead, Skel, Stat } from '../components/ui'
import { number, thaiDateTime } from '../lib/format'

/**
 * Ranked-choice voting over model verdicts (part 103). The server is the only place that counts:
 * this page renders the stored per-method breakdown (plurality, Borda, Condorcet) and the stored
 * pairwise matrix. Nothing is recalculated in the browser, so two reviewers looking at the same
 * round see the same winner and the same explanation.
 *
 * The page deliberately offers all three methods side by side. A ballot set can have a plurality
 * winner that is not the Borda winner while still having a Condorcet winner; hiding the losing
 * method would make that disagreement invisible.
 */

type VotingMethod = 'plurality' | 'borda' | 'condorcet'
type RoundStatus = 'open' | 'tallied'

interface VoteOption {
  key: string
  label_th: string
}

interface VoteRound {
  id: string
  title: string
  question: string
  options: VoteOption[]
  method: VotingMethod
  quorum: number
  ballot_count: number
  status: RoundStatus
  winner: string
  winner_label_th: string
  tallied_at?: string
  created_by?: string
  created_at: string
}

interface VoteBallot {
  id: string
  round_id: string
  model_code: string
  model_name_th: string
  rankings: string[]
  created_by?: string
  created_at: string
}

interface VoteStanding {
  rank: number
  key: string
  label_th: string
  first_choice: number
  borda_points: number
  head_to_head_wins: number
}

interface VoteMethodResult {
  method: VotingMethod
  winner: string
  winner_label_th: string
  tie: boolean
  decided_by: string
  standings: VoteStanding[]
}

interface VotePair {
  a: string
  b: string
  ab: number
  ba: number
  ties: number
  winner: string
}

interface VoteBreakdown {
  ballot_count: number
  quorum: number
  quorum_met: boolean
  method: VotingMethod
  winner: string
  winner_label_th: string
  decided_by: string
  tie_break: string
  condorcet_winner: string
  agreement: boolean
  disagreement: boolean
  method_results: VoteMethodResult[]
  pairwise: VotePair[]
  rationale_th: string
}

interface VoteResult {
  id: string
  round_id: string
  method: VotingMethod
  ballot_count: number
  quorum: number
  quorum_met: boolean
  winner: string
  winner_label_th: string
  plurality_winner: string
  borda_winner: string
  condorcet_winner: string
  agreement: boolean
  decided_by: string
  tie_break: string
  breakdown: VoteBreakdown
  created_by?: string
  created_at: string
}

interface VoteRoundDetail {
  round: VoteRound
  ballots: VoteBallot[]
  result: VoteResult | null
}

interface OptionDraft {
  key: string
  label: string
}

const ROUNDS_KEY = ['vote-rounds'] as const
const roundKey = (id: string | undefined) => ['vote-round', id] as const

const METHOD_ORDER: VotingMethod[] = ['plurality', 'borda', 'condorcet']

const METHOD_LABEL: Record<VotingMethod, string> = {
  plurality: 'เสียงข้างมากแรก',
  borda: 'คะแนนโบรดา',
  condorcet: 'คอนดอร์เซต์',
}

const METHOD_NOTE: Record<VotingMethod, string> = {
  plurality: 'นับเฉพาะตัวเลือกอันดับหนึ่งของแต่ละบัตร',
  borda: 'ให้ n-1 คะแนนแก่อันดับหนึ่ง ลดลงทีละหนึ่ง ตัวเลือกที่ไม่ถูกจัดลำดับได้ศูนย์',
  condorcet: 'เทียบทุกคู่แบบตัวต่อตัว ผู้ชนะต้องชนะทุกตัวเลือกอื่น',
}

const DECIDED_BY_LABEL: Record<string, string> = {
  score: 'คะแนนสูงสุด',
  condorcet: 'ชนะทุกคู่',
  tie_break_condorcet: 'เสมอ — เลือกผู้ชนะคอนดอร์เซต์',
  tie_break_borda: 'เสมอ — คะแนนโบรดาสูงกว่า',
  tie_break_plurality: 'เสมอ — เสียงข้างมากแรกมากกว่า',
  tie_break_stable_key: 'เสมอ — เรียงรหัสตัวเลือก',
  no_condorcet_winner: 'ไม่มีผู้ชนะคอนดอร์เซต์',
  condorcet_fallback_borda: 'ไม่มีผู้ชนะคอนดอร์เซต์ — ใช้โบรดา',
  no_quorum: 'ยังไม่ครบองค์ประชุม',
  no_ballots: 'ยังไม่มีบัตร',
}

const DEFAULT_OPTIONS: OptionDraft[] = [
  { key: 'approve', label: 'อนุมัติ' },
  { key: 'reject', label: 'ปฏิเสธ' },
  { key: 'escalate', label: 'ส่งต่อผู้ตรวจสอบ' },
]

const DEFAULT_FORM = {
  title: '',
  question: '',
  method: 'borda' as VotingMethod,
  quorum: '2',
}

const KEY_PATTERN = /^[a-z0-9][a-z0-9_-]{0,39}$/

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback
}

function shortID(id: string): string {
  return id.slice(0, 8)
}

function optionLabel(options: VoteOption[], key: string): string {
  if (!key) return '—'
  return options.find((option) => option.key === key)?.label_th ?? key
}

function methodLabel(method: string): string {
  return METHOD_LABEL[method as VotingMethod] ?? method
}

function decidedByLabel(value: string): string {
  return DECIDED_BY_LABEL[value] ?? value
}

function statusLabel(status: RoundStatus): string {
  return status === 'tallied' ? 'ปิดผลแล้ว' : 'เปิดรับบัตร'
}

export function Voting() {
  const qc = useQueryClient()
  const [selectedID, setSelectedID] = useState<string | null>(null)
  const [form, setForm] = useState(DEFAULT_FORM)
  const [options, setOptions] = useState<OptionDraft[]>(DEFAULT_OPTIONS)
  const [modelCode, setModelCode] = useState('')
  const [modelName, setModelName] = useState('')
  const [ranking, setRanking] = useState<string[]>([])
  const [notice, setNotice] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  const roundsQ = useQuery({
    queryKey: ROUNDS_KEY,
    queryFn: () => list<VoteRound>('/v1/voting/rounds?limit=50'),
    staleTime: 15_000,
  })
  const rounds = useMemo(() => roundsQ.data ?? [], [roundsQ.data])
  const activeID = selectedID ?? rounds[0]?.id
  const detailQ = useQuery({
    queryKey: roundKey(activeID),
    queryFn: () => api<VoteRoundDetail>(`/v1/voting/rounds/${activeID}`),
    enabled: Boolean(activeID),
    staleTime: 15_000,
  })
  const detail = detailQ.data
  const round = detail?.round ?? rounds.find((item) => item.id === activeID) ?? null
  const result = detail?.result ?? null

  useEffect(() => {
    setRanking([])
    setModelCode('')
    setModelName('')
  }, [activeID])

  const createRound = useMutation({
    mutationFn: () => {
      const quorum = Number(form.quorum)
      return apiWrite<VoteRound>('/v1/voting/rounds', 'POST', {
        title: form.title.trim(),
        question: form.question.trim(),
        method: form.method,
        quorum,
        options: options.map((option) => ({
          key: option.key.trim(),
          label_th: option.label.trim(),
        })),
      })
    },
    onSuccess: (created) => {
      setNotice(`เปิดรอบ ${created.title} แล้ว · วิธีหลัก ${methodLabel(created.method)} · องค์ประชุม ${created.quorum} บัตร`)
      setSelectedID(created.id)
      setForm(DEFAULT_FORM)
      setOptions(DEFAULT_OPTIONS.map((option) => ({ ...option })))
      void qc.invalidateQueries({ queryKey: ROUNDS_KEY })
    },
    onError: (err) => setActionError(errorText(err, 'เปิดรอบลงคะแนนไม่สำเร็จ')),
  })

  const castBallot = useMutation({
    mutationFn: () => {
      if (!activeID) throw new Error('ยังไม่ได้เลือกรอบลงคะแนน')
      return apiWrite<VoteBallot>(`/v1/voting/rounds/${activeID}/ballots`, 'POST', {
        model_code: modelCode.trim(),
        model_name_th: modelName.trim(),
        rankings: ranking,
      })
    },
    onSuccess: (ballot) => {
      setNotice(`บันทึกบัตรของ ${ballot.model_name_th || ballot.model_code} แล้ว · ${ballot.rankings.length} อันดับ`)
      setModelCode('')
      setModelName('')
      setRanking([])
      void qc.invalidateQueries({ queryKey: ROUNDS_KEY })
      if (activeID) void qc.invalidateQueries({ queryKey: roundKey(activeID) })
    },
    onError: (err) => setActionError(errorText(err, 'บันทึกบัตรลงคะแนนไม่สำเร็จ')),
  })

  const tally = useMutation({
    mutationFn: () => {
      if (!activeID) throw new Error('ยังไม่ได้เลือกรอบลงคะแนน')
      return apiWrite<VoteResult>(`/v1/voting/rounds/${activeID}/tally`, 'POST')
    },
    onSuccess: (stored) => {
      setNotice(
        stored.quorum_met
          ? `นับคะแนนแล้ว · ผู้ชนะ ${stored.winner_label_th || stored.winner} · ${decidedByLabel(stored.decided_by)}`
          : `นับคะแนนแล้ว แต่ยังไม่ครบองค์ประชุม (${stored.ballot_count}/${stored.quorum} บัตร) จึงยังไม่ประกาศผู้ชนะ`,
      )
      void qc.invalidateQueries({ queryKey: ROUNDS_KEY })
      if (activeID) void qc.invalidateQueries({ queryKey: roundKey(activeID) })
    },
    onError: (err) => setActionError(errorText(err, 'นับคะแนนไม่สำเร็จ')),
  })

  function updateOption(index: number, field: keyof OptionDraft, value: string) {
    setOptions((old) => old.map((option, i) => (i === index ? { ...option, [field]: value } : option)))
  }

  function validateRound(): string | null {
    if (!form.title.trim()) return 'ต้องระบุชื่อหัวข้อของรอบ'
    if (options.length < 2) return 'ต้องมีตัวเลือกอย่างน้อยสองรายการ'
    const keys = new Set<string>()
    for (const option of options) {
      const key = option.key.trim()
      if (!KEY_PATTERN.test(key)) {
        return `รหัสตัวเลือก "${key || '(ว่าง)'}" ต้องเป็นตัวพิมพ์เล็ก a-z, 0-9, _ หรือ - และเริ่มด้วยตัวอักษรหรือตัวเลข`
      }
      if (keys.has(key)) return `รหัสตัวเลือก ${key} ซ้ำกัน`
      keys.add(key)
      if (!option.label.trim()) return `ต้องระบุชื่อของตัวเลือก ${key}`
    }
    const quorum = Number(form.quorum)
    if (!Number.isInteger(quorum) || quorum < 1 || quorum > 200) {
      return 'องค์ประชุมต้องเป็นจำนวนเต็มตั้งแต่ 1 ถึง 200 บัตร'
    }
    return null
  }

  function submitRound() {
    setNotice(null)
    setActionError(null)
    const problem = validateRound()
    if (problem) {
      setActionError(problem)
      return
    }
    createRound.mutate()
  }

  function submitBallot() {
    setNotice(null)
    setActionError(null)
    if (!activeID || round?.status !== 'open') return
    if (!/^[A-Za-z0-9._:-]{1,64}$/.test(modelCode.trim())) {
      setActionError('รหัสโมเดลต้องเป็น a-z, A-Z, 0-9, จุด, _, - หรือ : และยาวไม่เกิน 64 ตัวอักษร')
      return
    }
    if (ranking.length === 0) {
      setActionError('เลือกตัวเลือกอย่างน้อยหนึ่งรายการเพื่อจัดลำดับ')
      return
    }
    castBallot.mutate()
  }

  function toggleRank(key: string) {
    setRanking((old) => (old.includes(key) ? old.filter((entry) => entry !== key) : [...old, key]))
  }

  const busy = createRound.isPending || castBallot.isPending || tally.isPending

  return (
    <>
      <PageHead
        title="ลงคะแนนหลายโมเดลแบบจัดลำดับ"
        sub="เปิดรอบคำถาม · ให้แต่ละโมเดลจัดลำดับตัวเลือก · นับสามวิธีพร้อมกัน (เสียงข้างมากแรก โบรดา คอนดอร์เซต์) · องค์ประชุม · กฎตัดสินเสมอที่กำหนดแน่นอน · เก็บผลแยกวิธีไว้ตรวจสอบ"
        actions={
          <button
            className="btn btn-ghost btn-sm"
            disabled={roundsQ.isFetching}
            onClick={() => {
              void qc.invalidateQueries({ queryKey: ROUNDS_KEY })
              if (activeID) void qc.invalidateQueries({ queryKey: roundKey(activeID) })
            }}
          >
            {roundsQ.isFetching ? 'กำลังโหลด…' : 'รีเฟรช'}
          </button>
        }
      />

      {notice ? (
        <div className="card card-pad" role="status" style={{ marginBottom: 14, borderColor: 'var(--color-hairline-strong)' }}>
          <span className="dim" style={{ fontSize: 12.5 }}>{notice}</span>
        </div>
      ) : null}

      {actionError ? (
        <div className="card card-pad" role="alert" style={{ marginBottom: 14, borderColor: 'var(--color-crimson)' }}>
          <span style={{ fontSize: 12.5, color: 'var(--color-crimson)' }}>{actionError}</span>
        </div>
      ) : null}

      <div className="grid-3 stagger">
        <Stat
          label="รอบที่บันทึกไว้"
          value={number(rounds.length)}
          unit="รอบ"
          foot="แต่ละรอบมีตัวเลือกและวิธีหลักคงที่ตั้งแต่เปิดรอบ"
        />
        <Stat
          label="รอบที่ปิดผลแล้ว"
          value={number(rounds.filter((item) => item.status === 'tallied').length)}
          unit="รอบ"
          foot="ผลที่บันทึกแล้วจะไม่ถูกนับซ้ำหรือแก้ไข"
        />
        <Stat
          label="บัตรในรอบที่เลือก"
          value={round ? `${number(round.ballot_count)}` : '—'}
          unit={round ? `/ ${number(round.quorum)}` : undefined}
          foot={round ? statusLabel(round.status) : 'เลือกรอบจากตารางด้านล่าง'}
        />
      </div>

      <form
        className="card card-pad"
        style={{ marginTop: 18 }}
        onSubmit={(event) => {
          event.preventDefault()
          submitRound()
        }}
      >
        <div className="row" style={{ alignItems: 'baseline' }}>
          <span className="card-label">เปิดรอบลงคะแนนใหม่</span>
          <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>
            ตัวเลือก 2–12 รายการ · รหัสใช้ในบัตรและผลลัพธ์ · เปลี่ยนภายหลังไม่ได้
          </span>
        </div>

        <div className="grid-2" style={{ marginTop: 6 }}>
          <label className="field">
            <span className="dim" style={{ fontSize: 11.5 }}>ชื่อหัวข้อ</span>
            <input
              className="input"
              value={form.title}
              maxLength={200}
              placeholder="เช่น ข้อสรุปกรณีหลักฐานขัดกัน"
              disabled={busy}
              onChange={(event) => setForm((old) => ({ ...old, title: event.target.value }))}
              required
            />
          </label>
          <label className="field">
            <span className="dim" style={{ fontSize: 11.5 }}>คำถาม (ไม่บังคับ)</span>
            <input
              className="input"
              value={form.question}
              maxLength={2000}
              placeholder="เช่น ควรปิดข้อสังเกตนี้ด้วยข้อสรุปใด"
              disabled={busy}
              onChange={(event) => setForm((old) => ({ ...old, question: event.target.value }))}
            />
          </label>
          <label className="field">
            <span className="dim" style={{ fontSize: 11.5 }}>วิธีหลักที่ใช้ประกาศผลรอบ</span>
            <select
              className="input"
              value={form.method}
              disabled={busy}
              onChange={(event) => setForm((old) => ({ ...old, method: event.target.value as VotingMethod }))}
            >
              {METHOD_ORDER.map((method) => (
                <option key={method} value={method}>{METHOD_LABEL[method]}</option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="dim" style={{ fontSize: 11.5 }}>องค์ประชุมขั้นต่ำ (บัตร)</span>
            <input
              className="input tnum"
              type="number"
              min={1}
              max={200}
              step={1}
              value={form.quorum}
              disabled={busy}
              onChange={(event) => setForm((old) => ({ ...old, quorum: event.target.value }))}
              required
            />
          </label>
        </div>

        <div style={{ marginTop: 16 }}>
          <div className="row" style={{ gap: 12 }}>
            <span className="card-label">ตัวเลือก</span>
            <button
              className="btn btn-ghost btn-sm"
              type="button"
              disabled={busy || options.length >= 12}
              onClick={() => setOptions((old) => [...old, { key: '', label: '' }])}
            >
              เพิ่มตัวเลือก
            </button>
            <span className="dim" style={{ fontSize: 11.5, marginLeft: 'auto' }}>
              {options.length}/12 รายการ
            </span>
          </div>
          <div className="stack" style={{ marginTop: 10, gap: 8 }}>
            {options.map((option, index) => (
              <div key={index} className="row" style={{ gap: 10 }}>
                <input
                  className="input mono"
                  style={{ width: 190 }}
                  value={option.key}
                  maxLength={40}
                  placeholder="option_key"
                  aria-label={`รหัสตัวเลือกที่ ${index + 1}`}
                  disabled={busy}
                  onChange={(event) => updateOption(index, 'key', event.target.value)}
                  required
                />
                <input
                  className="input"
                  style={{ flex: 1 }}
                  value={option.label}
                  maxLength={200}
                  placeholder="ชื่อตัวเลือกภาษาไทย"
                  aria-label={`ชื่อตัวเลือกที่ ${index + 1}`}
                  disabled={busy}
                  onChange={(event) => updateOption(index, 'label', event.target.value)}
                  required
                />
                <button
                  className="btn btn-ghost btn-sm"
                  type="button"
                  disabled={busy || options.length <= 2}
                  onClick={() => setOptions((old) => old.filter((_, i) => i !== index))}
                >
                  ลบ
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="row" style={{ marginTop: 16, gap: 12 }}>
          <button className="btn btn-primary" type="submit" disabled={busy || !form.title.trim()}>
            {createRound.isPending ? 'กำลังเปิดรอบ…' : 'เปิดรอบลงคะแนน'}
          </button>
          <span className="dim" style={{ fontSize: 11.5 }}>
            ระบบเก็บตัวเลือกและวิธีหลักไว้กับการ์ดรอบ ผลทุกวิธีจะถูกคำนวณและบันทึกพร้อมกันเมื่อกดนับคะแนน
          </span>
        </div>
        {createRound.isError ? <div className="form-error">{errorText(createRound.error, 'เปิดรอบไม่สำเร็จ')}</div> : null}
      </form>

      {roundsQ.isLoading ? (
        <div className="stack" style={{ marginTop: 18 }}>
          <Skel h={120} />
          <Skel h={240} />
        </div>
      ) : roundsQ.isError ? (
        <div className="card card-pad" style={{ marginTop: 18 }}>
          <Empty
            title="โหลดรอบลงคะแนนไม่สำเร็จ"
            body={errorText(roundsQ.error, 'เรียก /v1/voting/rounds ไม่สำเร็จ — ตรวจสอบว่าเส้นทางถูก mount แล้ว')}
          />
        </div>
      ) : rounds.length === 0 ? (
        <div className="card card-pad" style={{ marginTop: 18 }}>
          <Empty
            title="ยังไม่มีรอบลงคะแนน"
            body="กรอกชื่อหัวข้อ เลือกตัวเลือกอย่างน้อยสองรายการ แล้วกดเปิดรอบ ระบบจะเก็บรอบไว้ให้โมเดลลงบัตรและนับคะแนนต่อไป"
          />
        </div>
      ) : (
        <>
          <div className="card" style={{ marginTop: 18 }}>
            <div className="row" style={{ padding: '16px 20px', gap: 12 }}>
              <span className="card-label">รอบลงคะแนน</span>
              <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>
                เลือกแถวเพื่อดูบัตรและผลแยกวิธี
              </span>
            </div>
            <div className="table-wrap" style={{ border: 'none', borderRadius: 0 }}>
              <table className="data">
                <thead>
                  <tr>
                    <th>หัวข้อ</th>
                    <th style={{ width: 150 }}>วิธีหลัก</th>
                    <th style={{ width: 110 }}>บัตร</th>
                    <th style={{ width: 130 }}>สถานะ</th>
                    <th style={{ width: 200 }}>ผู้ชนะ</th>
                    <th style={{ width: 150 }}>เปิดเมื่อ</th>
                  </tr>
                </thead>
                <tbody>
                  {rounds.map((item) => (
                    <tr
                      key={item.id}
                      className={item.id === activeID ? 'active' : undefined}
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        setSelectedID(item.id)
                        setNotice(null)
                        setActionError(null)
                      }}
                    >
                      <td>
                        <div style={{ color: 'var(--color-ivory)' }}>{item.title}</div>
                        <div className="dim mono" style={{ fontSize: 10.5, marginTop: 4 }}>
                          {shortID(item.id)}
                        </div>
                      </td>
                      <td className="dim">{methodLabel(item.method)}</td>
                      <td className="tnum">{number(item.ballot_count)} / {number(item.quorum)}</td>
                      <td>
                        <span className={`chip ${item.status === 'tallied' ? 'chip-on' : ''}`}>
                          {statusLabel(item.status)}
                        </span>
                      </td>
                      <td>
                        {item.winner
                          ? <span style={{ color: 'var(--color-gold)' }}>{optionLabel(item.options, item.winner)}</span>
                          : <span className="dim">ยังไม่ประกาศ</span>}
                      </td>
                      <td className="dim">{thaiDateTime(item.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {detailQ.isLoading ? (
            <div className="stack" style={{ marginTop: 18 }}><Skel h={200} /><Skel h={260} /></div>
          ) : detailQ.isError ? (
            <div className="card card-pad" style={{ marginTop: 18 }}>
              <div className="form-error">{errorText(detailQ.error, 'โหลดรายละเอียดรอบไม่สำเร็จ')}</div>
            </div>
          ) : round && detail ? (
            <>
              <div className="card card-pad" style={{ marginTop: 18 }}>
                <div className="row" style={{ alignItems: 'baseline', gap: 12 }}>
                  <div>
                    <div className="card-label">รอบที่เลือก</div>
                    <div style={{ fontSize: 17, color: 'var(--color-ivory)', marginTop: 4 }}>{round.title}</div>
                    {round.question ? (
                      <div className="dim" style={{ fontSize: 12.5, marginTop: 6 }}>{round.question}</div>
                    ) : null}
                  </div>
                  <span className="chip" style={{ marginLeft: 'auto' }}>{methodLabel(round.method)}</span>
                  <span className={`chip ${round.status === 'tallied' ? 'chip-on' : ''}`}>{statusLabel(round.status)}</span>
                </div>
                <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginTop: 14 }}>
                  {round.options.map((option) => (
                    <span key={option.key} className="chip">
                      <span className="mono" style={{ fontSize: 10.5, marginRight: 6 }}>{option.key}</span>
                      {option.label_th}
                    </span>
                  ))}
                </div>
                <div className="grid-3" style={{ marginTop: 16 }}>
                  <div>
                    <div className="card-label">บัตรที่ส่งแล้ว</div>
                    <div className="stat-value" style={{ fontSize: 24 }}>
                      {number(round.ballot_count)}<span className="stat-unit">/ {number(round.quorum)}</span>
                    </div>
                  </div>
                  <div>
                    <div className="card-label">วิธีหลักของรอบ</div>
                    <div style={{ marginTop: 7, fontSize: 14 }}>{methodLabel(round.method)}</div>
                    <div className="dim" style={{ fontSize: 11.5, marginTop: 4 }}>{METHOD_NOTE[round.method]}</div>
                  </div>
                  <div>
                    <div className="card-label">ผลที่บันทึก</div>
                    <div style={{ marginTop: 7, fontSize: 14 }}>
                      {result ? (result.quorum_met ? (result.winner_label_th || result.winner) : 'ยังไม่ครบองค์ประชุม') : 'ยังไม่นับคะแนน'}
                    </div>
                    <div className="dim" style={{ fontSize: 11.5, marginTop: 4 }}>
                      {result ? decidedByLabel(result.decided_by) : 'กดนับคะแนนเมื่อบัตรครบตามต้องการ'}
                    </div>
                  </div>
                </div>
              </div>

              {detail.ballots.length > 0 ? (
                <div className="card" style={{ marginTop: 18 }}>
                  <div className="row" style={{ padding: '16px 20px', gap: 12 }}>
                    <span className="card-label">บัตรของแต่ละโมเดล</span>
                    <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>
                      อันดับ 1 คือความชอบสูงสุด · ตัวเลือกที่ไม่จัดลำดับถือว่าเสมอกันในอันดับสุดท้าย
                    </span>
                  </div>
                  <div className="table-wrap" style={{ border: 'none', borderRadius: 0 }}>
                    <table className="data">
                      <thead>
                        <tr>
                          <th style={{ width: 220 }}>โมเดล</th>
                          <th>ลำดับที่ส่ง</th>
                          <th style={{ width: 170 }}>เวลา</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detail.ballots.map((ballot) => (
                          <tr key={ballot.id} style={{ cursor: 'default' }}>
                            <td>
                              <div style={{ color: 'var(--color-ivory)' }}>{ballot.model_name_th || ballot.model_code}</div>
                              <div className="mono dim" style={{ fontSize: 10.5, marginTop: 4 }}>{ballot.model_code}</div>
                            </td>
                            <td>
                              <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                                {ballot.rankings.map((key, index) => (
                                  <span key={key} className="chip">
                                    <span className="tnum" style={{ marginRight: 6 }}>{index + 1}</span>
                                    {optionLabel(round.options, key)}
                                  </span>
                                ))}
                              </div>
                            </td>
                            <td className="dim">{thaiDateTime(ballot.created_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : null}

              {round.status === 'open' ? (
                <form
                  className="card card-pad"
                  style={{ marginTop: 18 }}
                  onSubmit={(event) => {
                    event.preventDefault()
                    submitBallot()
                  }}
                >
                  <div className="row" style={{ alignItems: 'baseline' }}>
                    <span className="card-label">ส่งบัตรของโมเดล</span>
                    <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>
                      หนึ่งโมเดลหนึ่งบัตร · คลิกตัวเลือกเพื่อเพิ่มลำดับ คลิกซ้ำเพื่อนำออก
                    </span>
                  </div>
                  <div className="grid-2" style={{ marginTop: 6 }}>
                    <label className="field">
                      <span className="dim" style={{ fontSize: 11.5 }}>รหัสโมเดล</span>
                      <input
                        className="input mono"
                        value={modelCode}
                        maxLength={64}
                        placeholder="เช่น severity_prior"
                        disabled={busy}
                        onChange={(event) => setModelCode(event.target.value)}
                        required
                      />
                    </label>
                    <label className="field">
                      <span className="dim" style={{ fontSize: 11.5 }}>ชื่อโมเดล (ไม่บังคับ)</span>
                      <input
                        className="input"
                        value={modelName}
                        maxLength={200}
                        placeholder="เช่น โมเดลลำดับความรุนแรง"
                        disabled={busy}
                        onChange={(event) => setModelName(event.target.value)}
                      />
                    </label>
                  </div>

                  <div style={{ marginTop: 12 }}>
                    <div className="row" style={{ gap: 10 }}>
                      <span className="dim" style={{ fontSize: 11.5 }}>ลำดับที่ต้องการ</span>
                      <button
                        className="btn btn-ghost btn-sm"
                        type="button"
                        disabled={busy || ranking.length === 0}
                        onClick={() => setRanking([])}
                      >
                        ล้างลำดับ
                      </button>
                    </div>
                    <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginTop: 9 }}>
                      {round.options.map((option) => {
                        const position = ranking.indexOf(option.key)
                        return (
                          <button
                            key={option.key}
                            type="button"
                            className={`btn btn-sm ${position >= 0 ? 'btn-primary' : 'btn-ghost'}`}
                            disabled={busy}
                            onClick={() => toggleRank(option.key)}
                            aria-pressed={position >= 0}
                          >
                            {position >= 0 ? `${position + 1}. ` : ''}{option.label_th}
                          </button>
                        )
                      })}
                    </div>
                    <div className="dim" style={{ fontSize: 11.5, marginTop: 9 }}>
                      {ranking.length === 0
                        ? 'ยังไม่ได้เลือกตัวเลือก — บัตรต้องมีอย่างน้อยหนึ่งอันดับ'
                        : `อันดับ: ${ranking.map((key) => optionLabel(round.options, key)).join(' > ')}`}
                    </div>
                  </div>

                  <div className="row" style={{ marginTop: 16, gap: 12 }}>
                    <button className="btn btn-primary" type="submit" disabled={busy || ranking.length === 0}>
                      {castBallot.isPending ? 'กำลังบันทึก…' : 'ส่งบัตรลงคะแนน'}
                    </button>
                    <span className="dim" style={{ fontSize: 11.5 }}>
                      ระบบตรวจว่ารหัสตัวเลือกมีจริง ไม่ซ้ำ และโมเดลนี้ยังไม่เคยส่งบัตรในรอบนี้
                    </span>
                  </div>
                  {castBallot.isError ? <div className="form-error">{errorText(castBallot.error, 'ส่งบัตรไม่สำเร็จ')}</div> : null}
                </form>
              ) : null}

              {round.status === 'open' ? (
                <div className="card card-pad" style={{ marginTop: 18 }}>
                  <div className="row" style={{ gap: 12 }}>
                    <div>
                      <div className="card-label">ปิดรอบและนับคะแนน</div>
                      <div className="dim" style={{ fontSize: 12, marginTop: 5 }}>
                        ระบบจะนับทั้งสามวิธีจากบัตรที่บันทึกไว้ แล้วล็อกผลหนึ่งชุดต่อรอบ
                      </div>
                    </div>
                    <button
                      className="btn btn-primary"
                      style={{ marginLeft: 'auto' }}
                      disabled={busy}
                      onClick={() => {
                        setNotice(null)
                        setActionError(null)
                        tally.mutate()
                      }}
                    >
                      {tally.isPending ? 'กำลังนับ…' : 'นับคะแนนและปิดผล'}
                    </button>
                  </div>
                  {tally.isError ? <div className="form-error">{errorText(tally.error, 'นับคะแนนไม่สำเร็จ')}</div> : null}
                </div>
              ) : null}

              {result ? (
                <>
                  <div className="card card-pad" style={{ marginTop: 18, borderColor: 'var(--color-hairline-strong)' }}>
                    <div className="row" style={{ alignItems: 'baseline', gap: 12 }}>
                      <div>
                        <div className="card-label">ผลของรอบ</div>
                        <div className="stat-value" style={{ fontSize: 30, marginTop: 4 }}>
                          {result.quorum_met
                            ? (result.winner_label_th || optionLabel(round.options, result.winner))
                            : 'ยังไม่ประกาศผู้ชนะ'}
                        </div>
                        <div className="dim" style={{ fontSize: 12, marginTop: 6 }}>
                          {result.breakdown.rationale_th}
                        </div>
                      </div>
                      <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
                        <span className={`chip ${result.quorum_met ? 'chip-on' : ''}`}>
                          {result.quorum_met ? 'ครบองค์ประชุม' : 'ไม่ครบองค์ประชุม'}
                        </span>
                        <div className="dim" style={{ fontSize: 11.5, marginTop: 8 }}>
                          {number(result.ballot_count)} / {number(result.quorum)} บัตร
                        </div>
                      </div>
                    </div>

                    {result.breakdown.disagreement ? (
                      <div
                        role="status"
                        style={{
                          marginTop: 14,
                          padding: '10px 13px',
                          fontSize: 12.5,
                          border: '1px solid var(--color-gold)',
                          borderRadius: 6,
                          color: 'var(--color-gold)',
                        }}
                      >
                        วิธีเสียงข้างมากแรกเลือก {optionLabel(round.options, result.plurality_winner)}
                        {' '}แต่โบรดาเลือก {optionLabel(round.options, result.borda_winner)}
                        {result.condorcet_winner
                          ? ` · ผู้ชนะคอนดอร์เซต์คือ ${optionLabel(round.options, result.condorcet_winner)}`
                          : ' · ไม่มีผู้ชนะคอนดอร์เซต์'}
                      </div>
                    ) : null}

                    {result.tie_break ? (
                      <div className="dim" style={{ fontSize: 12, marginTop: 12 }}>
                        กฎตัดสินเสมอ: {result.tie_break}
                      </div>
                    ) : null}
                  </div>

                  <div className="grid-3 stagger" style={{ marginTop: 18 }}>
                    {METHOD_ORDER.map((method) => {
                      const item = result.breakdown.method_results.find((entry) => entry.method === method)
                      if (!item) return null
                      return (
                        <div key={method} className="card">
                          <div style={{ padding: '16px 18px 10px' }}>
                            <div className="row" style={{ gap: 8 }}>
                              <span className="card-label">{METHOD_LABEL[method]}</span>
                              {method === round.method ? <span className="chip chip-on">วิธีหลัก</span> : null}
                            </div>
                            <div style={{ fontSize: 15, color: 'var(--color-ivory)', marginTop: 8 }}>
                              {item.winner
                                ? (item.winner_label_th || optionLabel(round.options, item.winner))
                                : 'ไม่มีผู้ชนะ'}
                            </div>
                            <div className="dim" style={{ fontSize: 11.5, marginTop: 4 }}>
                              {decidedByLabel(item.decided_by)}{item.tie ? ' · คะแนนสูงสุดเสมอกัน' : ''}
                            </div>
                          </div>
                          <div className="table-wrap" style={{ border: 'none', borderRadius: 0 }}>
                            <table className="data">
                              <thead>
                                <tr>
                                  <th style={{ width: 38 }}>#</th>
                                  <th>ตัวเลือก</th>
                                  <th style={{ width: 54 }}>อันดับ 1</th>
                                  <th style={{ width: 58 }}>โบรดา</th>
                                  <th style={{ width: 54 }}>ชนะคู่</th>
                                </tr>
                              </thead>
                              <tbody>
                                {item.standings.map((row) => (
                                  <tr key={row.key} style={{ cursor: 'default' }}>
                                    <td className="tnum dim">{row.rank}</td>
                                    <td>
                                      <span style={{ color: row.key === item.winner ? 'var(--color-gold)' : undefined }}>
                                        {row.label_th || optionLabel(round.options, row.key)}
                                      </span>
                                    </td>
                                    <td className="tnum">{number(row.first_choice)}</td>
                                    <td className="tnum">{number(row.borda_points)}</td>
                                    <td className="tnum">{number(row.head_to_head_wins)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          <div className="dim" style={{ padding: '10px 18px 16px', fontSize: 11 }}>
                            {METHOD_NOTE[method]}
                          </div>
                        </div>
                      )
                    })}
                  </div>

                  <div className="card" style={{ marginTop: 18 }}>
                    <div className="row" style={{ padding: '16px 20px', gap: 12 }}>
                      <span className="card-label">ผลเทียบรายคู่</span>
                      <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>
                        ตัวเลขคือจำนวนบัตรที่จัดอันดับ A เหนือ B · “เสมอ” คือบัตรที่ไม่ได้จัดทั้งสองตัวเลือก
                      </span>
                    </div>
                    <div className="table-wrap" style={{ border: 'none', borderRadius: 0 }}>
                      <table className="data">
                        <thead>
                          <tr>
                            <th>A</th>
                            <th>B</th>
                            <th style={{ width: 100 }}>A เหนือ B</th>
                            <th style={{ width: 100 }}>B เหนือ A</th>
                            <th style={{ width: 90 }}>เสมอ</th>
                            <th style={{ width: 180 }}>ผลคู่</th>
                          </tr>
                        </thead>
                        <tbody>
                          {result.breakdown.pairwise.map((pair) => (
                            <tr key={`${pair.a}-${pair.b}`} style={{ cursor: 'default' }}>
                              <td>{optionLabel(round.options, pair.a)}</td>
                              <td>{optionLabel(round.options, pair.b)}</td>
                              <td className="tnum">{number(pair.ab)}</td>
                              <td className="tnum">{number(pair.ba)}</td>
                              <td className="tnum">{number(pair.ties)}</td>
                              <td>
                                {pair.winner
                                  ? <span style={{ color: 'var(--color-gold)' }}>{optionLabel(round.options, pair.winner)}</span>
                                  : <span className="dim">เสมอกัน</span>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              ) : null}
            </>
          ) : null}
        </>
      )}
    </>
  )
}
